import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';

export interface StockChangeRecord {
  id: string;
  spuCode: string;
  spuName: string;
  skuCode: string;
  categoryName: string;
  warehouseCode: string;
  warehouseName: string;
  recordTypeCode: string;
  recordTypeMeaning: string;
  warehouseTypeCode: string;
  warehouseTypeMeaning: string;
  qtyTypeCode: string;
  qtyTypeMeaning: string;
  preRealNum: number;
  changeNum: number;
  aftRealNum: number;
  createdBy: number;
  creationDate: string;
  createdName: string;
  recordCode: string;
  orderCode: string;
  reasonCode: string;
  reasonMeaning: string;
  actor: string;
}

/** Values must match warehouse.typeCode returned by stock-service (WarehouseType). */
export const WAREHOUSE_TYPE_OPTIONS = [
  {
    label: 'inventory_change_lookup.table.warehouse_types.MAIN',
    value: 'MAIN',
  },
  {
    label: 'inventory_change_lookup.table.warehouse_types.STORE',
    value: 'STORE',
  },
  {
    label: 'inventory_change_lookup.table.warehouse_types.COD',
    value: 'COD',
  },
];

export const TRANSACTION_TYPE_OPTIONS = [
  {
    value: 'adjustRecord',
    label: 'inventory_change_lookup.search.transaction_types.adjust_record',
    description: 'Inventory adjustment record',
    orderSeq: 0,
  },
  {
    value: 'allotRecord',
    label: 'inventory_change_lookup.search.transaction_types.allot_record',
    description: 'Inventory transfer record',
    orderSeq: 0,
  },
  {
    value: 'moveRecord',
    label: 'inventory_change_lookup.search.transaction_types.move_record',
    description: 'Record of moving stock between warehouses',
    orderSeq: 0,
  },
  {
    value: 'reserveRecord',
    label: 'inventory_change_lookup.search.transaction_types.reserve_record',
    description: 'Record of reserved/locked stock',
    orderSeq: 0,
  },
  {
    value: 'distributePlan',
    label: 'inventory_change_lookup.search.transaction_types.distribute_plan',
    description: 'Plan for warehouse distribution',
    orderSeq: 0,
  },
  {
    value: 'async',
    label: 'inventory_change_lookup.search.transaction_types.async',
    description: 'Record of inventory sync',
    orderSeq: 0,
  },
  {
    value: 'preemptionRecord',
    label: 'inventory_change_lookup.search.transaction_types.preemption_record',
    description: 'Record of pre-allocated stock',
    orderSeq: 0,
  },
  {
    value: 'rescissionPreemptionRecord',
    label:
      'inventory_change_lookup.search.transaction_types.rescission_preemption_record',
    description: 'Record of released/cancelled pre-allocation',
    orderSeq: 0,
  },
  {
    value: 'resultRecord',
    label: 'inventory_change_lookup.search.transaction_types.result_record',
    description: 'Record of processing result',
    orderSeq: 0,
  },
  {
    value: 'sourcingRecord',
    label: 'inventory_change_lookup.search.transaction_types.sourcing_record',
    description: 'Record of inventory sourcing',
    orderSeq: 0,
  },
  {
    value: 'relieveSourcingRecord',
    label:
      'inventory_change_lookup.search.transaction_types.relieve_sourcing_record',
    description: 'Record of released sourcing',
    orderSeq: 0,
  },
];

export const STOCK_CHANGE_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'spuCode',
    label: 'inventory_change_lookup.search.fields.product_code',
    type: 'text',
    placeholder: 'inventory_change_lookup.search.fields.product_code',
  },
  {
    key: 'spuName',
    label: 'inventory_change_lookup.search.fields.product_name',
    type: 'text',
    placeholder: 'inventory_change_lookup.search.fields.product_name',
  },
  {
    key: 'skuCode',
    label: 'inventory_change_lookup.search.fields.sku_code',
    type: 'text',
    placeholder: 'inventory_change_lookup.search.fields.sku_code',
  },
  {
    key: 'categoryId',
    label: 'inventory_change_lookup.search.fields.product_category',
    type: 'multiselect',
    placeholder: 'inventory_change_lookup.search.fields.product_category',
    options: [], // Will be populated from API
  },
  {
    key: 'warehouseCode',
    label: 'inventory_change_lookup.search.fields.warehouse_code',
    type: 'text',
    placeholder: 'inventory_change_lookup.search.fields.warehouse_code',
  },
  {
    key: 'warehouseName',
    label: 'inventory_change_lookup.search.fields.warehouse_name',
    type: 'text',
    placeholder: 'inventory_change_lookup.search.fields.warehouse_name',
  },
  {
    key: 'recordTypeCodeList',
    label: 'inventory_change_lookup.search.fields.transaction_type',
    type: 'multiselect',
    placeholder: 'inventory_change_lookup.search.fields.transaction_type',
    options: TRANSACTION_TYPE_OPTIONS, // Will be populated from API
  },
  {
    key: 'warehouseTypeCodeList',
    label: 'inventory_change_lookup.search.fields.warehouse_type',
    type: 'multiselect',
    placeholder: 'inventory_change_lookup.search.fields.warehouse_type',
    options: WAREHOUSE_TYPE_OPTIONS, // Will be populated from API
  },
  {
    key: 'changeTime',
    label: 'inventory_change_lookup.search.fields.change_time',
    type: 'dateRange',
    range: { startKey: 'beginDate', endKey: 'endDate' },
  },
  {
    key: 'recordCode',
    label: 'inventory_change_lookup.search.fields.reference_document',
    type: 'text',
    placeholder: 'inventory_change_lookup.search.fields.reference_document',
  },
];

/** Default columns (always visible on first load). */
export const STOCK_CHANGE_DEFAULT_COLUMNS_ORDER: string[] = [
  'spuCode',
  'spuName',
  'skuCode',
  'warehouseCode',
  'warehouseName',
  'recordTypeMeaning',
  'warehouseTypeMeaning',
  'qtyTypeMeaning',
  'preRealNum',
  'changeNum',
  'aftRealNum',
  'recordCode',
];

/** Flexible columns (can be shown/hidden via column config). */
export const STOCK_CHANGE_FLEXIBLE_COLUMNS_ORDER: string[] = [
  'categoryName',
  'createdName',
  'creationDate',
  'orderCode',
  'reasonMeaning',
  'actor',
];

/** Full column order for column config panel. */
export const STOCK_CHANGE_REFERENCE_COLUMNS_ORDER: string[] = [
  'spuCode',
  'spuName',
  'skuCode',
  'categoryName',
  'warehouseCode',
  'warehouseName',
  'recordTypeMeaning',
  'warehouseTypeMeaning',
  'qtyTypeMeaning',
  'preRealNum',
  'changeNum',
  'aftRealNum',
  'recordCode',
  'orderCode',
  'reasonMeaning',
  'actor',
  'createdName',
  'creationDate',
];

export const STOCK_CHANGE_TABLE_CONFIG: TableColumn[] = [
  {
    label: 'inventory_change_lookup.table.columns.product_code',
    prop: 'spuCode',
    width: 160,
  },
  {
    label: 'inventory_change_lookup.table.columns.product_name',
    prop: 'spuName',
    width: 160,
  },
  {
    label: 'inventory_change_lookup.table.columns.sku_code',
    prop: 'skuCode',
    width: 160,
  },
  {
    label: 'inventory_change_lookup.table.columns.product_category',
    prop: 'categoryName',
    width: 160,
  },
  {
    label: 'inventory_change_lookup.table.columns.warehouse_code',
    prop: 'warehouseCode',
    width: 160,
  },
  {
    label: 'inventory_change_lookup.table.columns.warehouse_name',
    prop: 'warehouseName',
    width: 160,
  },
  {
    label: 'inventory_change_lookup.table.columns.transaction_type',
    prop: 'recordTypeMeaning',
    width: 160,
  },
  {
    label: 'inventory_change_lookup.table.columns.warehouse_type',
    prop: 'warehouseTypeMeaning',
    width: 120,
  },
  {
    label: 'inventory_change_lookup.table.columns.inventory_type',
    prop: 'qtyTypeMeaning',
    width: 160,
  },
  {
    label: 'inventory_change_lookup.table.columns.before_change_qty',
    prop: 'preRealNum',
    width: 90,
  },
  {
    label: 'inventory_change_lookup.table.columns.changed_qty',
    prop: 'changeNum',
    width: 90,
  },
  {
    label: 'inventory_change_lookup.table.columns.after_change_qty',
    prop: 'aftRealNum',
    width: 90,
  },
  {
    label: 'inventory_change_lookup.table.columns.reference_document',
    prop: 'recordCode',
    width: 160,
  },
  {
    label: 'inventory_change_lookup.table.columns.order_code',
    prop: 'orderCode',
    width: 160,
  },
  {
    label: 'inventory_change_lookup.table.columns.reason_code',
    prop: 'reasonMeaning',
    width: 160,
  },
  {
    label: 'inventory_change_lookup.table.columns.actor',
    prop: 'actor',
    width: 135,
  },
  {
    label: 'inventory_change_lookup.table.columns.operator',
    prop: 'createdName',
    width: 135,
  },
  {
    label: 'inventory_change_lookup.table.columns.operation_time',
    prop: 'creationDate',
    width: 135,
  },
];

export const STOCK_CHANGE_DEFAULT_CONFIG = {
  PAGE_SIZE: 20,
} as const;

export const STOCK_CHANGE_MESSAGES = {
  ERROR: {
    DATA_LOAD_FAILED: 'Failed to load data',
  },
} as const;
